// Admin: invoice reminder scheduler settings + manual "sweep now" button.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Bell, Play, Save } from "lucide-react";
import {
  adminGetReminderSettings,
  adminSaveReminderSettings,
  adminRunReminderSweepNow,
} from "@/lib/api/invoice-reminders.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/invoice-reminders")({
  head: () => ({ meta: [{ title: "Admin · Invoice reminders" }] }),
  component: RemindersPage,
});

function RemindersPage() {
  const load = useServerFn(adminGetReminderSettings);
  const save = useServerFn(adminSaveReminderSettings);
  const run = useServerFn(adminRunReminderSweepNow);

  const { data, isLoading, refetch } = useQuery({ queryKey: ["reminder-settings"], queryFn: () => load() });
  const [form, setForm] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<any>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  async function onSave() {
    if (!form) return;
    setSaving(true);
    try {
      await save({ data: {
        enabled: !!form.enabled,
        stage1_days: Number(form.stage1_days),
        stage2_days: Number(form.stage2_days),
        stage3_days: Number(form.stage3_days),
        subject_template: form.subject_template,
        body_template: form.body_template,
      }});
      setSavedAt(new Date().toLocaleTimeString());
      await refetch();
    } finally { setSaving(false); }
  }

  async function onRun() {
    setRunning(true);
    setRunResult(null);
    try {
      setRunResult(await run());
    } finally { setRunning(false); }
  }

  if (isLoading || !form) {
    return <div className="p-6 text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="mb-1 flex items-center gap-2 font-display text-2xl text-navy">
        <Bell className="h-5 w-5 text-gold" /> Invoice reminders
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Configure the automated email reminder schedule sent to clients with outstanding invoices. Placeholders: <code>{"{{reference}}"}</code>, <code>{"{{client_name}}"}</code>, <code>{"{{amount}}"}</code>, <code>{"{{due_date}}"}</code>, <code>{"{{pay_link}}"}</code>, <code>{"{{days_overdue}}"}</code>.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3">
            <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            <span className="text-sm">Reminders enabled</span>
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>1st reminder (days past due)</Label>
              <Input type="number" min={0} max={120} value={form.stage1_days}
                onChange={(e) => setForm({ ...form, stage1_days: e.target.value })} />
            </div>
            <div>
              <Label>2nd reminder</Label>
              <Input type="number" min={0} max={180} value={form.stage2_days}
                onChange={(e) => setForm({ ...form, stage2_days: e.target.value })} />
            </div>
            <div>
              <Label>Final reminder</Label>
              <Input type="number" min={0} max={365} value={form.stage3_days}
                onChange={(e) => setForm({ ...form, stage3_days: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Email subject template</Label>
            <Input value={form.subject_template}
              onChange={(e) => setForm({ ...form, subject_template: e.target.value })} />
          </div>
          <div>
            <Label>Email body template</Label>
            <Textarea rows={10} value={form.body_template}
              onChange={(e) => setForm({ ...form, body_template: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onSave} disabled={saving}>
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Save settings
            </Button>
            {savedAt ? <span className="text-xs text-emerald-700">Saved at {savedAt}</span> : null}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Run sweep now</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Manually trigger the reminder sweep. This runs automatically each day via cron.
          </p>
          <Button variant="outline" onClick={onRun} disabled={running}>
            {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
            Run reminder sweep
          </Button>
          {runResult ? (
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
{JSON.stringify(runResult, null, 2)}
            </pre>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
